import React, { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import SubscriptionPlans from '@/components/subscription/SubscriptionPlans';
import CurrentSubscription from '@/components/subscription/CurrentSubscription';
import SubscriptionLoading from '@/components/subscription/SubscriptionLoading';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { 
  Subscription, 
  subscriptionPlans, 
  getSubscriptionPlanByTier,
  getCurrentSubscription,
  getCurrentMonthLeadImports,
  SubscriptionPlan
} from '@/services/subscriptions';
import { getActiveSubscriptionPlans } from '@/services/subscriptionPlanService';
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PaymentHistory from '@/components/subscription/PaymentHistory';
import { supabase } from '@/integrations/supabase/client';

const SubscriptionsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [leadImports, setLeadImports] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeTier, setUpgradeTier] = useState(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>(subscriptionPlans);
  
  const fetchData = async () => {
    if (user) {
      setIsLoading(true);
      try {
        const [subscription, imports, dbPlans] = await Promise.all([
          getCurrentSubscription(),
          getCurrentMonthLeadImports(),
          getActiveSubscriptionPlans().catch(() => subscriptionPlans) // Fallback to hardcoded
        ]);
        
        setCurrentSubscription(subscription);
        setLeadImports(imports);
        
        // Update localStorage and notify other components
        if (subscription) {
          localStorage.setItem('subscription', JSON.stringify({
            tier: subscription.tier,
            status: subscription.status
          }));
          window.dispatchEvent(new Event('subscriptionUpdated'));
        }
        
        // Convert DB plans to match SubscriptionPlan interface
        if (dbPlans && dbPlans.length > 0) {
          const convertedPlans = dbPlans.map(p => ({
            id: p.tier,
            name: p.name,
            tier: p.tier,
            price: p.price / 100, // Convert from cents
            priceDisplay: p.price_display,
            description: p.description || '',
            features: p.features,
            leadLimit: p.lead_limit,
            recommended: p.is_recommended
          }));
          setPlans(convertedPlans);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        toast({
          title: "Error",
          description: "Failed to load subscription information",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Show cancel message if returned from Revolut
    if (searchParams.get('cancelled') === 'true') {
      toast({
        title: "Checkout canceled",
        description: "You can try again anytime.",
      });
      // Clean the query string so toast doesn't repeat on refresh
      navigate('/subscription', { replace: true });
    }
    
    fetchData();
  }, [user, toast, searchParams, navigate]);

  // Real-time subscription updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',  // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Subscription changed:', payload);
          fetchData();
        }
      )
      .subscribe();

    // Refetch on window focus (when returning from payment)
    const handleFocus = () => {
      fetchData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);
  
  // Auto-verify pending subscriptions
  useEffect(() => {
    const run = async () => {
      if (!currentSubscription?.stripeSubscriptionId) return;
      if (currentSubscription.status !== 'pending') return;

      try {
        await supabase.functions.invoke('verify-revolut-order', {
          body: { order_id: currentSubscription.stripeSubscriptionId, subscription_id: currentSubscription.id }
        });

        const fresh = await getCurrentSubscription();
        setCurrentSubscription(fresh);

        if (fresh?.status === 'active') {
          toast({
            title: 'Subscription Activated',
            description: 'Your subscription is now active!',
          });
          navigate('/app?payment_success=true', { replace: true });
        }
      } catch (e) {
        console.error('Auto verification error:', e);
      }
    };
    run();
  }, [currentSubscription, navigate, toast]);
  
  const handleUpgrade = async (tier) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Handle free tier separately - allow downgrade
    if (tier === 'free') {
      if (currentSubscription?.tier === 'free') {
        toast({
          title: "Already on Free Plan",
          description: "You're currently on the free plan.",
        });
        return;
      }
      
      // Allow immediate downgrade to free
      try {
        const { error } = await supabase
          .from('subscriptions')
          .update({
            tier: 'free',
            status: 'active',
            current_period_start: new Date().toISOString(),
            current_period_end: null,  // Free tier doesn't expire
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id)
          .eq('id', currentSubscription.id);
        
        if (error) throw error;
        
        toast({
          title: "Downgraded to Free",
          description: "Your subscription has been changed to the free tier.",
        });
        
        await fetchData();
      } catch (err) {
        console.error('Downgrade error:', err);
        toast({
          title: "Downgrade Failed",
          description: "Please contact support if this issue persists.",
          variant: "destructive",
        });
      }
      return;
    }
    
    // Check if already on this tier
    if (currentSubscription?.tier === tier) {
      toast({
        title: "Already Subscribed",
        description: `You're already on the ${tier} plan.`,
      });
      return;
    }
    
    const plan = plans.find(p => p.tier === tier);
    if (!plan) {
      toast({
        title: "Error",
        description: "Invalid subscription plan selected.",
        variant: "destructive",
      });
      return;
    }
    
    // Navigate to payment page in same tab
    const paymentUrl = `/payment?tier=${plan.tier}&price=${plan.price}&name=${encodeURIComponent(plan.name)}&leadLimit=${plan.leadLimit}`;
    navigate(paymentUrl);
  };
  
  const handleManualVerify = async () => {
    if (!currentSubscription?.stripeSubscriptionId) return;

    try {
      await supabase.functions.invoke('verify-revolut-order', {
        body: { order_id: currentSubscription.stripeSubscriptionId, subscription_id: currentSubscription.id }
      });

      const fresh = await getCurrentSubscription();
      setCurrentSubscription(fresh);

      if (fresh?.status === 'active') {
        toast({
          title: 'Subscription Activated',
          description: 'Your subscription is now active!',
        });
        navigate('/app?payment_success=true', { replace: true });
      } else {
        toast({
          title: 'Still processing',
          description: 'We are finalizing your subscription. Please try again in a few seconds.',
        });
      }
    } catch (err) {
      console.error('Manual verification error:', err);
      toast({
        title: 'Verification failed',
        description: 'Could not verify payment. Please try again.',
        variant: 'destructive',
      });
    }
  };
  
  const currentPlan = currentSubscription
    ? getSubscriptionPlanByTier(currentSubscription.tier)
    : subscriptionPlans[0];
  
  const leadQuotaPercentage = currentPlan 
    ? currentPlan.leadLimit === Infinity 
      ? 0  // Unlimited - show empty bar or hide it
      : Math.min(100, Math.round((leadImports / currentPlan.leadLimit) * 100))
    : 0;
  
  return (
    <div className="min-h-screen bg-gray-50/50">
      <main className="container mx-auto px-4 py-8 md:py-12 max-w-6xl">
        <div className="text-center mb-10 md:mb-12">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Choose Your Plan
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Select the perfect plan for your lead generation needs
          </p>
        </div>
        
      {isLoading ? (
        <SubscriptionLoading />
      ) : (
        <div className="space-y-10">
          {currentSubscription && (
            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200/50 shadow-md">
              <CardContent className="p-6 md:p-8">
                <CurrentSubscription 
                  subscription={currentSubscription}
                  plan={currentPlan}
                  leadImports={leadImports}
                  leadQuotaPercentage={leadQuotaPercentage}
                />
              </CardContent>
            </Card>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SubscriptionPlans 
              plans={plans}
              currentSubscriptionTier={currentSubscription?.tier}
              isUpgrading={isUpgrading}
              upgradeTier={upgradeTier}
              handleUpgrade={handleUpgrade}
            />
          </div>
          
          {currentSubscription?.status === 'pending' && currentSubscription?.stripeSubscriptionId && (
            <Card className="border-yellow-300 bg-yellow-50/50 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-100 p-3 rounded-full">
                    <AlertCircle className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Payment Pending</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Your payment is being processed. This usually completes within a few minutes.
                      If you've completed the payment, click the button below to check the status.
                    </p>
                    <Button onClick={handleManualVerify} variant="default" size="sm" className="shadow-md">
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Check Payment Status
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader className="border-b pb-4">
              <CardTitle className="text-xl">Payment History</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <PaymentHistory />
            </CardContent>
          </Card>
        </div>
      )}
      </main>
    </div>
  );
};

export default SubscriptionsPage;
